/**
 * useProducts.js — TanStack Query hooks for the Products & Dashboard API
 * ========================================================================
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { productService, dashboardService, qrService, adminService } from '@/api/productService';
import toast from 'react-hot-toast';
import { useLanguage } from '@/components/LanguageContext';
import { apiMessage } from '@/api/apiMessage';

// ── Products ────────────────────────────────────────────────────────────────

export const useProducts = () =>
  useQuery({
    queryKey: ['products'],
    queryFn: productService.list,
    select: (data) => data.results ?? data,
  });

export const useProduct = (productId) =>
  useQuery({
    queryKey: ['product', productId],
    queryFn: () => productService.get(productId),
    enabled: !!productId,
  });

export const useProductStats = (productId) =>
  useQuery({
    queryKey: ['product-stats', productId],
    queryFn: () => productService.stats(productId),
    enabled: !!productId,
  });

export const useUpdateProduct = () => {
  const qc = useQueryClient();
  const { t } = useLanguage();
  return useMutation({
    mutationFn: (variables) => {
      const { productId, fields } = variables;
      return productService.update(productId, fields);
    },
    onSuccess: (_, variables) => {
      const { productId } = variables;
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['product', productId] });
      toast.success(t('api.messages.product_updated'));
    },
    onError: (err) => {
      toast.error(apiMessage(err, t, t('api.errors.product_update_failed')));
    },
  });
};

export const useClaimProduct = () => {
  const qc = useQueryClient();
  const { t } = useLanguage();
  return useMutation({
    mutationFn: (qrId) => productService.claim(qrId),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
      toast.success(apiMessage(data, t, t('api.messages.device_claimed')));
    },
    onError: (err) => {
      toast.error(apiMessage(err, t, t('api.errors.claim_failed')));
    },
  });
};

// ── Dashboard ───────────────────────────────────────────────────────────────

export const useDashboardSummary = () =>
  useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: dashboardService.summary,
    staleTime: 60_000,
  });

export const useDashboardChart = (days = 30) =>
  useQuery({
    queryKey: ['dashboard-chart', days],
    queryFn: () => dashboardService.chart(days),
    select: (data) => data.series ?? [],
    staleTime: 60_000,
  });

export const useDashboardDevices = () =>
  useQuery({
    queryKey: ['dashboard-devices'],
    queryFn: dashboardService.devices,
    select: (data) => data.devices ?? [],
    staleTime: 60_000,
  });

export const useDashboardLocations = () =>
  useQuery({
    queryKey: ['dashboard-locations'],
    queryFn: dashboardService.locations,
    select: (data) => data.locations ?? [],
    staleTime: 60_000,
  });

// ── QR ──────────────────────────────────────────────────────────────────────

export const useQRScan = () =>
  useMutation({
    mutationFn: qrService.scan,
  });

export const useQRCheck = (qrId) =>
  useQuery({
    queryKey: ['qr-check', qrId],
    queryFn: () => qrService.check(qrId),
    enabled: !!qrId,
  });

export const usePublicTodoEn1 = (qrId) =>
  useQuery({
    queryKey: ['public-todo-en-1', qrId],
    queryFn: () => qrService.getPublicConfig(qrId),
    enabled: !!qrId,
    retry: 1,
    staleTime: 0,
    gcTime: 0
  });

// ── Admin ──────────────────────────────────────────────────────────────────

/**
 * Alertas del panel, con detalle. Para el contador del menú usa
 * `useAdminAlertsCount`, que comparte prefijo de clave (`['admin-alerts']`) para
 * que invalidar el prefijo refresque las dos.
 */
export const useAdminAlerts = ({ enabled = true } = {}) =>
  useQuery({
    queryKey: ['admin-alerts', 'detail'],
    queryFn: () => adminService.alerts(),
    enabled,
    staleTime: 60 * 1000,
  });

/**
 * Posponer / deshacer una fila de Alertas. Invalidan el prefijo
 * `['admin-alerts']` para que se refresquen a la vez la lista y el badge del menú.
 */
export const useSnoozeAlert = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => adminService.snoozeAlert(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-alerts'] }),
  });
};

export const useUnsnoozeAlert = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => adminService.unsnoozeAlert(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-alerts'] }),
  });
};

/**
 * Scoreboard de cuentas: se calcula una vez por noche, así que no hay nada que
 * refrescar más a menudo que al volver a la página.
 */
export const useAdminScoreboard = ({ enabled = true } = {}) =>
  useQuery({
    queryKey: ['admin-scoreboard'],
    queryFn: () => adminService.scoreboard(),
    enabled,
    staleTime: 10 * 60 * 1000,
    // Mientras hay un cálculo en marcha (el «Recalcular ahora» dura ~1,5 min)
    // se pregunta cada 10 s para enseñar el resultado en cuanto acabe.
    refetchInterval: (query) => (
      query.state.data?.latestRun?.status === 'running' ? 10 * 1000 : false
    ),
  });

/** «Recalcular ahora»: encola el cálculo y refresca el board para ver que está en marcha. */
export const useRecomputeScoreboard = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => adminService.recomputeScoreboard(),
    onSuccess: () => {
      // El run nuevo tarda un instante en aparecer como `running`; se vuelve a
      // pedir enseguida y el sondeo de arriba hace el resto.
      setTimeout(() => qc.invalidateQueries({ queryKey: ['admin-scoreboard'] }), 1500);
    },
  });
};

/**
 * Solo el total, para el badge del menú. Se pide en cada carga de página del
 * admin, de ahí el `staleTime` y el `enabled` (un no-admin recibiría un 403).
 */
export const useAdminAlertsCount = ({ enabled = true } = {}) =>
  useQuery({
    queryKey: ['admin-alerts', 'count'],
    queryFn: () => adminService.alerts({ countsOnly: true }),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

export const useAdminUsers = (params = {}) =>
  useQuery({
    queryKey: ['admin-users', params],
    queryFn: () => adminService.users(params),
    select: (data) => (data.results ? data : { results: data, count: data.length }),
  });

export const useAdminUserDetail = (userId) =>
  useQuery({
    queryKey: ['admin-user', userId],
    queryFn: () => adminService.userDetail(userId),
    enabled: !!userId,
  });

/**
 * Sube el logo de marca del informe. Invalida la ficha para que la tarjeta
 * repinte con los metadatos nuevos (fecha, quién lo puso, dimensiones).
 */
/**
 * Sube el logo del informe a UN ámbito: la cuenta entera, el informe de marca o
 * un local con plan. `mutate({ file, scope })`.
 */
export const useUploadReportLogo = (userId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, scope }) => adminService.uploadReportLogo(userId, file, scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });
    },
  });
};

/** Quita el logo de UN ámbito. `mutate(scope)`; no toca los demás. */
export const useDeleteReportLogo = (userId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scope) => adminService.deleteReportLogo(userId, scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });
    },
  });
};

export const useAdminUserGbusinessConnections = (userId) =>
  useQuery({
    queryKey: ['admin-user', userId, 'gbusiness-connections'],
    queryFn: () => adminService.getGbusinessConnections(userId),
    enabled: !!userId,
  });

/**
 * Da o quita el plan Business a UN local del usuario, desde la ficha de admin.
 *
 * El `remove` es la salida de soporte y la única que hay: para el cliente, dar
 * un asiento es una decisión de ida y el backend rechaza cualquier lista que
 * quite uno. Su pantalla le dice «contacta con soporte», y esto es lo que
 * soporte pulsa.
 *
 * Invalida `['admin-user', userId]` entero —no solo las conexiones— porque el
 * contador de asientos de la cabecera sale del detalle del usuario.
 *
 * @param {string} userId
 */
export const useAdminUserSeat = (userId) => {
  const queryClient = useQueryClient();
  const { t } = useLanguage();
  return useMutation({
    // Anotado porque `useMutation` sin parámetros de tipo infiere `void` para
    // las variables, y desestructurar `void` son dos errores de tsc. Los hooks
    // de más arriba los arrastran; este no hace falta que los sume.
    /** @param {{locationId: string, remove: boolean}} vars */
    mutationFn: (vars) => (
      vars.remove
        ? adminService.releaseSeatFromLocation(userId, vars.locationId)
        : adminService.assignSeatToLocation(userId, vars.locationId)
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });
    },
    onError: (e) => toast.error(apiMessage(e, t)),
  });
};

// Los tres bloques de contexto comercial de la ficha. Cada uno es su propia
// query -y su propia tarjeta con spinner- para que el detalle siga abriendo
// igual de rapido y un fallo en Places o en las metricas no se lleve la pagina.
// Cuelgan de ['admin-user', userId] a proposito: la invalidacion que ya hacen
// las mutaciones del detalle los refresca sin tocarlas.
export const useAdminUserDevicesOverview = (userId) =>
  useQuery({
    queryKey: ['admin-user', userId, 'devices-overview'],
    queryFn: () => adminService.userDevicesOverview(userId),
    enabled: !!userId,
  });

export const useAdminUserGbpOverview = (userId) =>
  useQuery({
    queryKey: ['admin-user', userId, 'gbp-overview'],
    queryFn: () => adminService.userGbpOverview(userId),
    enabled: !!userId,
  });

export const useAdminUserGeogridScans = (userId) =>
  useQuery({
    queryKey: ['admin-user', userId, 'geogrid-scans'],
    queryFn: () => adminService.userGeogridScans(userId),
    enabled: !!userId,
  });

export const useAdminUserTrialOverview = (userId) =>
  useQuery({
    queryKey: ['admin-user', userId, 'trial-overview'],
    queryFn: () => adminService.userTrialOverview(userId),
    enabled: !!userId,
  });

/** Refresca la foto de Places de uno o varios locales. Es una mutacion y no
 *  una query porque cuesta dinero: solo se dispara desde el boton. */
export const useRefreshPlaceSnapshots = (userId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (placeIds) => adminService.refreshPlaceSnapshots(userId, placeIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-user', userId, 'devices-overview'] });
    },
  });
};

export const useUpdateAdminUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, fields }) => adminService.updateUser(userId, fields),
    onSuccess: (_, { userId }) => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-user', userId] });
      // Cambiar el closer escribe un evento billing.closer_* en el feed del
      // usuario — refréscalo para que aparezca sin recargar la página.
      qc.invalidateQueries({ queryKey: ['admin', 'user-activity'] });
    },
  });
};

/**
 * Estrella del panel: marca/desmarca al usuario como favorito del equipo y
 * guarda la nota. Una sola mutación para los dos sentidos porque el botón es
 * uno: `favorite` decide si va el PUT o el DELETE.
 *
 * Invalida la lista además del detalle: la estrella se pinta en las dos
 * pantallas y el filtro "solo favoritos" tiene que dejar de mostrar al que
 * acaban de desmarcar.
 */
export const useToggleUserFavorite = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, favorite, note = '' }) =>
      favorite
        ? adminService.setUserFavorite(userId, note)
        : adminService.unsetUserFavorite(userId),
    onSuccess: (_, { userId }) => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-user', userId] });
    },
  });
};

/** Códigos promocionales para el modal del enlace de pago. El backend ya los
 *  cachea unos minutos; aquí solo se evita repetir la petición al reabrirlo. */
export const usePaymentLinkPromoCodes = (userId, { enabled = true } = {}) => useQuery({
  queryKey: ['admin', 'payment-link-promo-codes', userId],
  queryFn: () => adminService.getPaymentLinkPromoCodes(userId),
  staleTime: 5 * 60 * 1000,
  enabled: Boolean(userId) && enabled,
});

export const useCreatePaymentLinkMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, payload }) => adminService.createPaymentLink(userId, payload),
    // Escribe billing.payment_link_created en el registro de actividad.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'user-activity'] }),
  });
};

export const useAnnualSwitchPreviewMutation = () => useMutation({
  mutationFn: ({ userId, family }) => adminService.previewAnnualSwitch(userId, family),
});

export const useSwitchToAnnualMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, payload }) => adminService.switchToAnnual(userId, payload),
    // Tanto si sale como si no, hay línea nueva en el registro de actividad
    // (`billing.annual_switch_done` / `_failed`) y el estado puede haber cambiado.
    onSettled: (_data, _error, { userId }) => {
      qc.invalidateQueries({ queryKey: ['admin', 'user-activity'] });
      qc.invalidateQueries({ queryKey: ['admin-user', userId] });
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
};

export const useAssignProMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, payload }) => adminService.assignPro(userId, payload),
    onSuccess: (_, { userId }) => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-user', userId] });
      // Escribe billing.pro_assigned (+ billing.closer_* si cambió el closer).
      qc.invalidateQueries({ queryKey: ['admin', 'user-activity'] });
      // Un cobro fuera de plataforma mueve MRR e ingresos del periodo.
      qc.invalidateQueries({ queryKey: ['admin-metrics'] });
    },
  });
};

export const useRevokeProMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId }) => adminService.revokePro(userId),
    onSuccess: (_, { userId }) => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-user', userId] });
    },
  });
};

/** Bloqueo de usuarios (§162). Las tres escriben en el feed de actividad. */
export const useBlockUserMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, reason }) => adminService.blockUser(userId, reason),
    onSuccess: (_, { userId }) => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-user', userId] });
      qc.invalidateQueries({ queryKey: ['admin', 'user-activity'] });
    },
  });
};

export const useUnblockUserMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId }) => adminService.unblockUser(userId),
    onSuccess: (_, { userId }) => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-user', userId] });
      qc.invalidateQueries({ queryKey: ['admin', 'user-activity'] });
    },
  });
};

export const useDeleteBlockedUserMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId }) => adminService.deleteBlockedUser(userId),
    onSuccess: (_, { userId }) => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.removeQueries({ queryKey: ['admin-user', userId] });
    },
  });
};

/**
 * Regeneración manual de los informes del mes anterior. Solo invalida el feed
 * de actividad: el render va por Celery y tarda minutos, así que refrescar el
 * detalle del usuario no traería nada nuevo — lo único que ya ha cambiado al
 * volver la respuesta es la línea de audit.
 */
export const useRebuildMonthlyReportsMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId }) => adminService.rebuildMonthlyReports(userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'user-activity'] });
    },
  });
};

export const useDeleteAdminUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId) => adminService.deleteUser(userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
};

export const useAdminProducts = (params = {}) =>
  useQuery({
    queryKey: ['admin-products', params],
    queryFn: () => adminService.products(params),
    select: (data) => (data.results ? data : { results: data, count: data.length }),
  });

export const useAdminProductDetail = (productId) =>
  useQuery({
    queryKey: ['admin-product', productId],
    queryFn: () => adminService.productDetail(productId),
    enabled: !!productId,
  });

export const useUpdateAdminProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, fields }) => adminService.updateProduct(productId, fields),
    onSuccess: (_, { productId }) => {
      qc.invalidateQueries({ queryKey: ['admin-products'] });
      qc.invalidateQueries({ queryKey: ['admin-product', productId] });
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-user'] }); // Refetches current detail if open
      // Editing a device from the admin panel logs a device.updated event into
      // the owner's activity feed — refetch it so the AdminUserDetail log shows it.
      qc.invalidateQueries({ queryKey: ['admin', 'user-activity'] });
    },
  });
};

export const useDeleteAdminProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (productId) => adminService.deleteProduct(productId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-products'] });
    },
  });
};

export const useAdminStats = () =>
  useQuery({
    queryKey: ['admin-stats'],
    queryFn: adminService.stats,
  });

// ── Métricas (dirección de producto) ─────────────────────────────────────────
// Un hook por endpoint/pestaña con su propio staleTime. `params` = { from, to,
// granularity, include_trialing }; va en la queryKey para refetch al filtrar.
//
// Son consultas CARAS (varias recorren colecciones enteras), así que:
// - `options.enabled`: la página solo activa la de la pestaña abierta. Antes se
//   pedían las 11 a la vez en cada cambio de fecha.
// - `signal`: si el rango cambia antes de que vuelva, se aborta.
// - `retry: 0`: repetir una consulta que ya agotó el tiempo solo duplica la carga.
// - `keepPreviousData`: al cambiar de rango se siguen viendo las cifras
//   anteriores en vez de volver al spinner.
const adminMetricsQuery = (key, fetcher, staleTime) => (params = {}, options = {}) =>
  useQuery({
    queryKey: ['admin-metrics', key, params],
    queryFn: ({ signal }) => fetcher(params, signal),
    staleTime,
    retry: 0,
    placeholderData: keepPreviousData,
    ...options,
  });

export const useAdminMetricsOverview = adminMetricsQuery('overview', adminService.metricsOverview, 5 * 60 * 1000);
export const useAdminMetricsGrowth = adminMetricsQuery('growth', adminService.metricsGrowth, 5 * 60 * 1000);
export const useAdminMetricsActivation = adminMetricsQuery('activation', adminService.metricsActivation, 5 * 60 * 1000);
export const useAdminMetricsEngagement = adminMetricsQuery('engagement', adminService.metricsEngagement, 3 * 60 * 1000);
export const useAdminMetricsMonetization = adminMetricsQuery('monetization', adminService.metricsMonetization, 5 * 60 * 1000);
export const useAdminMetricsChurn = adminMetricsQuery('churn', adminService.metricsChurn, 30 * 60 * 1000);
export const useAdminMetricsContent = adminMetricsQuery('content', adminService.metricsContent, 5 * 60 * 1000);
export const useAdminMetricsWidgets = adminMetricsQuery('widgets', adminService.metricsWidgets, 5 * 60 * 1000);
export const useAdminMetricsClosers = adminMetricsQuery('closers', adminService.metricsClosers, 5 * 60 * 1000);
export const useAdminMetricsGeogrid = adminMetricsQuery('geogrid', adminService.metricsGeogrid, 5 * 60 * 1000);
export const useAdminMetricsOnboarding = adminMetricsQuery('onboarding', adminService.metricsOnboarding, 5 * 60 * 1000);

// La ficha de referencia no depende del periodo ni de la base de datos: son las
// constantes del código. `Infinity` porque solo cambia con un despliegue.
export const useAdminMetricsGeogridReference = (options = {}) =>
  useQuery({
    queryKey: ['admin-metrics', 'geogrid-reference'],
    queryFn: adminService.metricsGeogridReference,
    staleTime: Infinity,
    ...options,
  });

export const useAdminMapData = () =>
  useQuery({
    queryKey: ['admin-map-data'],
    queryFn: adminService.mapData,
  });

export const useAdminBulkHistory = (params = {}) =>
  useQuery({
    queryKey: ['admin-bulk-history', params],
    queryFn: () => adminService.bulkHistory(params),
  });

export const useCreateBulkProducts = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => adminService.createBulkProducts(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-bulk-history'] });
      qc.invalidateQueries({ queryKey: ['admin-products'] });
    },
  });
};

export const useUpdateBulkProductPlatform = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ historyId, platform }) => adminService.updateBulkPlatform(historyId, { platform }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-bulk-history'] });
      qc.invalidateQueries({ queryKey: ['admin-products'] });
    },
  });
};

export const useAdminGmb = () =>
  useQuery({
    queryKey: ['admin-gmb'],
    queryFn: adminService.gmb,
  });

export const useAdminSettings = () =>
  useQuery({
    queryKey: ['admin-settings'],
    queryFn: adminService.getSettings,
  });

export const useUpdateAdminSettings = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => adminService.updateSettings(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-settings'] });
      qc.invalidateQueries({ queryKey: ['public-settings'] });
    },
  });
};

export const usePublicSettings = () =>
  useQuery({
    queryKey: ['public-settings'],
    queryFn: adminService.getPublicSettings,
  });

export const useAdminMetricsCta = adminMetricsQuery('cta', adminService.metricsCta, 5 * 60 * 1000);

// ── Links de CTA de marketing (generador no-code) ───────────────────────────

/**
 * El catálogo de CTAs, interno y/o de campaña.
 *
 * `params.kind` acota la familia (`internal` | `external`); sin él vienen las
 * dos. Va DENTRO de la queryKey porque cada pestaña del panel pide la suya y
 * comparten caché: sin eso, cambiar de pestaña devolvería la lista de la otra.
 */
export const useCtaLinks = (params = {}) =>
  useQuery({
    queryKey: ['admin-cta-links', params],
    queryFn: () => adminService.ctaLinks(params),
  });

/**
 * El desglose por pantalla de UN CTA. `enabled` para que solo se pida al
 * desplegar la fila: son catorce filas de detalle por CTA y pedirlas con el
 * listado sería tirar N consultas que casi nunca se miran.
 */
export const useCtaPages = (ctaId, enabled = true) =>
  useQuery({
    queryKey: ['admin-cta-pages', ctaId],
    queryFn: () => adminService.ctaPages(ctaId),
    enabled: !!ctaId && enabled,
  });

/**
 * Qué botón cerró las altas de UNA campaña. Mismo `enabled` y mismo motivo que
 * `useCtaPages`: solo se pide al desplegar la fila.
 */
export const useCtaVia = (ctaId, enabled = true) =>
  useQuery({
    queryKey: ['admin-cta-via', ctaId],
    queryFn: () => adminService.ctaVia(ctaId),
    enabled: !!ctaId && enabled,
  });

export const useCreateCtaLink = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => adminService.createCtaLink(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-cta-links'] });
    },
  });
};

export const useUpdateCtaLink = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ctaId, ...payload }) => adminService.updateCtaLink(ctaId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-cta-links'] });
    },
  });
};
